import { useCallback } from 'react';
import Highcharts from 'highcharts/highstock';

/**
 * Binds each chart's own x-axis to its own window-change handler, with NO
 * cross-driving between the two charts. The main and ratio charts' time
 * windows are now fully independent: zooming/panning/clicking a range-
 * selector button on EITHER chart moves only that chart and fires only that
 * chart's `onWindowChange`, so the other chart's window, overlays, and y-axis
 * are left completely untouched.
 *
 * (The charts used to be kept in lockstep — the main chart drove the ratio
 * chart's extremes — but syncing the windows was unwanted; each chart's
 * avg/±1σ overlay is a statistic of its OWN visible window, so the two are
 * independent lenses on the same data.)
 *
 * Returns two `callback` functions to pass as `HighchartsReact`'s `callback`
 * prop (fired once, at chart creation, by the wrapper — the right place to
 * bind axis events since the same chart instance is reused across
 * re-renders via `chart.update()`, never recreated).
 *
 * @param {React.RefObject} chartRef - ref to the main chart's HighchartsReact instance
 * @param {React.RefObject} ratioChartRef - ref to the ratio chart's HighchartsReact instance
 * @param {(min: number|null, max: number|null) => void} onMainWindowChange
 *   fired when the MAIN chart's window changes (and once at attach time)
 * @param {(min: number|null, max: number|null) => void} onRatioWindowChange
 *   fired when the RATIO chart's window changes (and once at attach time)
 */
const useAxisSync = (chartRef, ratioChartRef, onMainWindowChange, onRatioWindowChange) => {
  // Each chart drives only its own window handler — no reference to the other
  // chart at all, so the two windows can never move in lockstep.
  const attach = useCallback((thisChart, onWindowChange) => {
    Highcharts.addEvent(thisChart.xAxis[0], 'afterSetExtremes', function (e) {
      onWindowChange(e.min, e.max);
    });

    // Picks up the default range-selector window without waiting for a
    // user interaction.
    const { min, max } = thisChart.xAxis[0].getExtremes();
    onWindowChange(min, max);
  }, []);

  const mainChartCallback = useCallback(
    (chart) => attach(chart, onMainWindowChange),
    [attach, onMainWindowChange]
  );
  const ratioChartCallback = useCallback(
    (chart) => attach(chart, onRatioWindowChange),
    [attach, onRatioWindowChange]
  );

  return { mainChartCallback, ratioChartCallback };
};

export default useAxisSync;
